import React from "react";
import { motion } from "framer-motion";
import PropTypes from "prop-types";

// --- Data Configuration ---
// All skills are organized here. Easy to update!
const skillsData = [
  {
    id: 1,
    subtitle: "Tech Stack",
    title: "Frameworks & Libraries",
    skills: [
      { label: "React", percentage: "90%" },
      { label: "Redux", percentage: "90%" },
      { label: "Node.js", percentage: "85%" },
      { label: "Express.js", percentage: "85%" },
      { label: "Bootstrap", percentage: "85%" },
      { label: "Tailwind CSS", percentage: "85%" },
      { label: "Material UI", percentage: "85%" },
      { label: "Firebase", percentage: "80%" },
      { label: "REST API Development", percentage: "85%" },
      { label: "ExcelJS", percentage: "80%" },
      { label: "Git / Version Control", percentage: "90%" },
    ],
  },
  {
    id: 2,
    subtitle: "Core Proficiencies",
    title: "Languages & Databases",
    skills: [
      { label: "HTML", percentage: "95%" },
      { label: "CSS", percentage: "90%" },
      { label: "JavaScript", percentage: "90%" },
      { label: "TypeScript", percentage: "85%" }, // Added as a good practice
      { label: "MySQL", percentage: "85%" },
      { label: "MongoDB", percentage: "90%" },
      { label: "SQL", percentage: "90%" },
      { label: "PHP", percentage: "80%" },
      { label: "Java", percentage: "80%" },
      { label: "Python", percentage: "75%" },
      { label: "XML", percentage: "85%" },
    ],
  },
  {
    id: 3,
    subtitle: "Infrastructure",
    title: "DevOps & Cloud Tools",
    skills: [
      { label: "Docker", percentage: "90%" },
      { label: "Kubernetes", percentage: "85%" },
      { label: "Helm", percentage: "80%" },
      { label: "Terraform", percentage: "85%" },
      { label: "Ansible", percentage: "80%" },
      { label: "Jenkins", percentage: "85%" },
      { label: "AWS / Azure", percentage: "85%" },
      { label: "Prometheus / Grafana", percentage: "80%" },
      { label: "ELK Stack", percentage: "75%" },
      { label: "Bash Scripting", percentage: "85%" },
    ],
  },
];

// --- Reusable Components ---

/**
 * A single animated skill bar.
 * @param {object} props - The component props.
 * @param {string} props.label - The name of the skill.
 * @param {string} props.percentage - The skill proficiency percentage.
 * @param {number} props.index - The index for calculating animation delay.
 */
const SkillBar = ({ label, percentage, index }) => {
  // Calculate a unique delay for each bar for a staggered animation
  const delay = 0.1 * index;

  return (
    <div className="overflow-x-hidden">
      <p className="text-sm uppercase font-medium">{label}</p>
      <span className="w-full h-2 bgOpacity rounded-md inline-flex mt-2">
        <motion.span
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: delay }}
          className="h-full bg-gradient-to-r from-blue-600 via-pink-500 to-red-500 rounded-md relative"
          style={{ width: percentage }}
        >
          <span className="absolute -top-7 right-0">{percentage}</span>
        </motion.span>
      </span>
    </div>
  );
};

SkillBar.propTypes = {
  label: PropTypes.string.isRequired,
  percentage: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

/**
 * A component to render a category of skills.
 * @param {object} props - The component props.
 * @param {object} props.category - The category data object.
 */
const SkillCategory = ({ category }) => {
  return (
    <div className="w-full lgl:w-1/3">
      <div className="py-12 font-titleFont flex flex-col gap-4">
        <p className="text-sm text-designColor tracking-[4px] uppercase">
          {category.subtitle}
        </p>
        <h2 className="text-3xl md:text-4xl font-bold">{category.title}</h2>
      </div>
      <div className="w-full flex flex-col gap-6">
        {category.skills.map((skill, index) => (
          <SkillBar
            key={skill.label}
            label={skill.label}
            percentage={skill.percentage}
            index={index}
          />
        ))}
      </div>
    </div>
  );
};

SkillCategory.propTypes = {
  category: PropTypes.shape({
    subtitle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        percentage: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
};

// --- Main Component ---

const Skills = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      className="w-full flex flex-col lgl:flex-row gap-10 lgl:gap-20"
    >
      {skillsData.map((category) => (
        <SkillCategory key={category.id} category={category} />
      ))}
    </motion.div>
  );
};

export default Skills;
